import type { DecisionAgentDomain } from '../decision-agent-platform';
import { defineModuleEvidenceContract, defineModuleEvidenceEntry } from './moduleEvidenceContract';
import { DECISION_AGENT_SOURCE_CLASSES } from './sourceClasses';

// origin/main literals: apps/api/src/decision-modules/carrier-transportation-cost/
// carrier-transportation-cost.data-lake-source.ts
// (CARRIER_TRANSPORTATION_COST_DATA_LAKE_CONTEXT_KIND, …_SCHEMA_VERSION_V2).
//
// Confirmed live on tenant helix_retail 2026-08-08 (read-only): 3 APPROVED,
// ingest-anchored rows at _v2 (latest approvedAt 2026-07-29) plus 1 older row at
// _v1. The freshest _v2 artifact's own state.evidenceRefs carry exactly
// carrier_rate_card, shipment_lane_spend, carrier_contract_terms, and
// strategy_margin_floor — every one gapStatus=available, confidence=high — which
// are precisely the four keys the manifest declares in requiredState and the four
// registered below. (_v2 also carries gl_account_decomposition at medium
// confidence; it is not a manifest requiredState key, so it is not registered.)
//
// SCHEMA VERSION CHOICE: the engine accepts both _v1 and _v2
// (CARRIER_TRANSPORTATION_COST_DATA_LAKE_SCHEMA_VERSIONS), but this substrate
// resolves one exact schemaVersion per entry. _v2 is registered because it is
// what the live tenant's freshest approved rows carry — the same choice #4521
// made for productivity-lean-ai.
//
// ⚠️ DISCLOSED FALSE-GREEN RISK (#4449 lane 1, 2026-08-08) — READ BEFORE TRUSTING
// THIS MODULE'S EVIDENCE COLUMN. The live artifact's real `asOf` is 2026-06-30:
// 39 days old against these keys' `weekly` (10-day) cadence, i.e. genuinely
// STALE. The readiness instrument classifies freshness from
// `contentJson->>'asOf'` at the TOP level, but carrier nests its as-of under
// `state.asOf`. The instrument therefore reads null and returns PRESENT. So this
// module will show GREEN because the instrument is not looking, NOT because the
// data is fresh. Registering the contract is still correct — the identity is
// real and CONTRACT_ABSENT was simply wrong — but the green must not be read as
// a freshness attestation until either (a) the snapshot is refreshed, or (b) the
// instrument is taught to read nested `state.asOf` (which will correctly turn
// this and other modules amber).
export const CARRIER_TRANSPORTATION_COST_EVIDENCE_KEYS = {
  carrierRateCard: 'carrier_rate_card',
  shipmentLaneSpend: 'shipment_lane_spend',
  carrierContractTerms: 'carrier_contract_terms',
  strategyMarginFloor: 'strategy_margin_floor',
} as const;

type CarrierTransportationCostEvidenceKey =
  (typeof CARRIER_TRANSPORTATION_COST_EVIDENCE_KEYS)[keyof typeof CARRIER_TRANSPORTATION_COST_EVIDENCE_KEYS];

export const CARRIER_TRANSPORTATION_COST_CONTEXT_KINDS = {
  governedSnapshot: 'decision_agent:carrier_transportation_cost:governed_snapshot',
} as const;

export const CARRIER_TRANSPORTATION_COST_SCHEMA_VERSIONS = {
  governedSnapshotV2: 'carrier_transportation_cost_governed_snapshot_v2',
} as const;

const NO_CARRIER_BINDING_PLACEHOLDERS: readonly string[] = Object.freeze([]);

// Mirrors carrier-transportation-cost.manifest.ts requiredState exactly.
const CARRIER_DOMAIN_BY_EVIDENCE_KEY: Readonly<
  Record<CarrierTransportationCostEvidenceKey, DecisionAgentDomain>
> = {
  carrier_rate_card: 'operations',
  shipment_lane_spend: 'operations',
  carrier_contract_terms: 'legal',
  strategy_margin_floor: 'operations',
};

const CARRIER_REQUIRED_FOR_BY_EVIDENCE_KEY = {
  carrier_rate_card: ['scenario', 'decision_pack', 'activation'],
  shipment_lane_spend: ['monitoring', 'scenario', 'decision_pack', 'activation', 'ask_helix'],
  carrier_contract_terms: ['scenario', 'decision_pack', 'activation'],
  strategy_margin_floor: ['scenario', 'decision_pack', 'activation'],
} as const;

const carrierEntry = (
  evidenceKey: CarrierTransportationCostEvidenceKey,
  description: string,
) =>
  defineModuleEvidenceEntry(evidenceKey, {
    sourceClass: DECISION_AGENT_SOURCE_CLASSES.connectorDerived,
    contextKind: CARRIER_TRANSPORTATION_COST_CONTEXT_KINDS.governedSnapshot,
    schemaVersion: CARRIER_TRANSPORTATION_COST_SCHEMA_VERSIONS.governedSnapshotV2,
    bindingPlaceholders: NO_CARRIER_BINDING_PLACEHOLDERS,
    domain: CARRIER_DOMAIN_BY_EVIDENCE_KEY[evidenceKey],
    freshnessCadence: 'weekly',
    requiredFor: CARRIER_REQUIRED_FOR_BY_EVIDENCE_KEY[evidenceKey],
    missingBehavior: 'block',
    description,
  });

export const CARRIER_TRANSPORTATION_COST_MODULE_EVIDENCE_CONTRACT =
  defineModuleEvidenceContract(
    'carrier-transportation-cost',
    {
      carrier_rate_card: carrierEntry(
        CARRIER_TRANSPORTATION_COST_EVIDENCE_KEYS.carrierRateCard,
        'Approved carrier rate card carried by the governed transportation snapshot; the ' +
          'contracted rate basis every renegotiation and rate-variance intent measures against.',
      ),
      shipment_lane_spend: carrierEntry(
        CARRIER_TRANSPORTATION_COST_EVIDENCE_KEYS.shipmentLaneSpend,
        'Approved shipment-and-lane spend carried by the same governed snapshot; the spend base ' +
          'for lane economics, concentration, and mode-shift reads.',
      ),
      carrier_contract_terms: carrierEntry(
        CARRIER_TRANSPORTATION_COST_EVIDENCE_KEYS.carrierContractTerms,
        'Approved carrier contract terms carried by the same governed snapshot; bounds which ' +
          'renegotiation and exit options are contractually reachable.',
      ),
      strategy_margin_floor: carrierEntry(
        CARRIER_TRANSPORTATION_COST_EVIDENCE_KEYS.strategyMarginFloor,
        'Approved strategy margin floor carried by the same governed snapshot; the governed ' +
          'constraint lane-level margin actions are tested against.',
      ),
    },
    {
      evidenceKeys: Object.values(CARRIER_TRANSPORTATION_COST_EVIDENCE_KEYS),
      sourceClasses: [DECISION_AGENT_SOURCE_CLASSES.connectorDerived],
      contextKinds: Object.values(CARRIER_TRANSPORTATION_COST_CONTEXT_KINDS),
      schemaVersions: Object.values(CARRIER_TRANSPORTATION_COST_SCHEMA_VERSIONS),
      stateKeys: Object.values(CARRIER_TRANSPORTATION_COST_EVIDENCE_KEYS),
      bindingPlaceholders: NO_CARRIER_BINDING_PLACEHOLDERS,
    },
  );

export type CarrierTransportationCostModuleEvidenceContract =
  typeof CARRIER_TRANSPORTATION_COST_MODULE_EVIDENCE_CONTRACT;
